/**
 * WHEN MOONFALL PAUSES NEW AI WORK, SAY SO PLAINLY (the owner, 2026-09-28:
 * "make these a bit more honest ... it's something we should say we're
 * working to fix").
 *
 * The loss brakes and the operator's global fuses refuse a new match or a
 * creation before anything is charged. The server sends WHO is paused and
 * UNTIL WHEN (game/utils/servicePause.js); this module says it in the
 * player's language and on the player's own clock:
 *
 *   free_daily / free_monthly  free play has a daily or monthly limit because
 *                              every match runs on AI we pay for; paying
 *                              players keep playing (one action: the passes)
 *   all_daily / all_monthly    Moonfall reached its safety limit; we are
 *                              working on it
 *   all_unplanned              paused while we fix a problem; no date
 *
 * It is NOT the Moonlight wall. The wall explains the player's OWN dark moon;
 * a pause is ours, and blaming their moon for it would be the dishonesty this
 * replaces. So it is its own dialog on its own layer, dressed like the wall
 * (auth.css) so it reads as a place in the game rather than an error box.
 */

const SERVICE_PAUSE_ID = 'service-pause';
const SERVICE_PAUSE_LAYER_ID = 'service-pause-layer';

const PAUSES = Object.freeze(['free_daily', 'free_monthly', 'all_daily', 'all_monthly', 'all_unplanned']);

/** @type {Record<string, [string, string]>} */
const TITLES = {
  free_daily: ['servicePause.title.freeDaily', 'Free play is paused until {{time}}'],
  free_monthly: ['servicePause.title.freeMonthly', 'Free play is paused until {{date}}'],
  all_daily: ['servicePause.title.allDaily', 'New matches are paused until {{time}}'],
  all_monthly: ['servicePause.title.allMonthly', 'New matches are paused until {{date}}'],
  all_unplanned: ['servicePause.title.allUnplanned', 'New matches are paused'],
};

/** @type {Record<string, [string, string]>} */
const BODIES = {
  free_daily: ['servicePause.body.freeDaily', 'Every match runs on AI that we pay for, so free play has a daily limit while Moonfall grows, and today\'s is used up. We\'re working to raise it. Paying players keep playing.'],
  free_monthly: ['servicePause.body.freeMonthly', 'Every match runs on AI that we pay for, so free play has a monthly limit while Moonfall grows, and this month\'s is used up. We\'re working to raise it. Paying players keep playing.'],
  all_daily: ['servicePause.body.allDaily', 'Moonfall reached its safety limit for how much AI it can run today. We\'re working on it. Matches already under way continue, and trying to start this one didn\'t use any of your Moonlight.'],
  all_monthly: ['servicePause.body.allMonthly', 'Moonfall reached its safety limit for how much AI it can run this month. We\'re working on it. This pause may also interrupt matches already under way. Trying to start this one didn\'t use any of your Moonlight.'],
  all_unplanned: ['servicePause.body.allUnplanned', 'We\'ve paused new matches while we fix a problem on our side, and we\'ll reopen as soon as we can. This pause may also interrupt matches already under way. Trying to start this one didn\'t use any of your Moonlight.'],
};

/** One line for a creation (Moon Fuse, Moon Dream, Stellar), where no dialog opens. */
/** @type {Record<string, [string, string]>} */
const CREATION_LINES = {
  free_daily: ['servicePause.creation.freeDaily', 'Free creation is paused until {{time}}. Every creation runs on AI that we pay for, so free play has a daily limit while Moonfall grows. No Stardust was spent. Paying players keep creating.'],
  free_monthly: ['servicePause.creation.freeMonthly', 'Free creation is paused until {{date}}. Every creation runs on AI that we pay for, so free play has a monthly limit while Moonfall grows. No Stardust was spent. Paying players keep creating.'],
  all_daily: ['servicePause.creation.allDaily', 'Creation is paused until {{time}}: Moonfall reached its safety limit for how much AI it can run today. We\'re working on it. No Stardust was spent.'],
  all_monthly: ['servicePause.creation.allMonthly', 'Creation is paused until {{date}}: Moonfall reached its safety limit for how much AI it can run this month. We\'re working on it. No Stardust was spent.'],
  all_unplanned: ['servicePause.creation.allUnplanned', 'Creation is paused while we fix a problem on our side. We\'re working on it. No Stardust was spent.'],
};

/** @param {unknown} payload */
function isServicePause(payload) {
  return Boolean(payload)
    && typeof payload === 'object'
    && PAUSES.includes(/** @type {{ pause?: string }} */ (payload).pause || '');
}

/**
 * The resume moment on the player's clock: a time for a daily pause (with the
 * weekday when it is not today where they are), a date for a monthly one.
 *
 * @param {unknown} resumesAt
 * @param {{ locale?: string, now?: number }} [options]
 * @returns {{ time: string, date: string }}
 */
function formatResume(resumesAt, { locale, now = Date.now() } = {}) {
  const at = Number(resumesAt);
  if (!Number.isFinite(at) || at <= 0) return { time: '', date: '' };
  const when = new Date(at);
  const today = new Date(now);
  const sameDay = when.getFullYear() === today.getFullYear()
    && when.getMonth() === today.getMonth()
    && when.getDate() === today.getDate();
  const tag = locale || undefined;
  try {
    const time = when.toLocaleTimeString(tag, { hour: 'numeric', minute: '2-digit' });
    const weekday = sameDay ? '' : when.toLocaleDateString(tag, { weekday: 'short' });
    return {
      time: weekday ? `${weekday} ${time}` : time,
      date: when.toLocaleDateString(tag, { month: 'long', day: 'numeric' }),
    };
  } catch (_) {
    return { time: '', date: '' };
  }
}

/**
 * The words for a pause, in the player's language.
 *
 * @param {{ pause?: string, resumesAt?: number|null }|null|undefined} payload
 * @param {(key: string, fallback: string, replacements?: Record<string, string>) => string} t
 * @param {{ locale?: string, now?: number }} [options]
 */
function servicePauseCopy(payload, t, { locale, now } = {}) {
  let pause = PAUSES.includes(payload?.pause || '') ? String(payload?.pause) : 'all_unplanned';
  const vars = formatResume(payload?.resumesAt, { locale, now });
  // A dated pause is always sent with its date. Should one ever arrive
  // without it, say the undated truth rather than "paused until ".
  const needsTime = pause === 'free_daily' || pause === 'all_daily';
  const needsDate = pause === 'free_monthly' || pause === 'all_monthly';
  if ((needsTime && !vars.time) || (needsDate && !vars.date)) pause = 'all_unplanned';
  const [titleKey, titleFallback] = TITLES[pause];
  const [bodyKey, bodyFallback] = BODIES[pause];
  const [lineKey, lineFallback] = CREATION_LINES[pause];
  return {
    pause,
    freeOnly: pause.startsWith('free_'),
    title: t(titleKey, titleFallback, vars),
    body: t(bodyKey, bodyFallback, vars),
    creation: t(lineKey, lineFallback, vars),
  };
}

/* WHEN A SERVICE MOONFALL DEPENDS ON IS BUSY OR DOWN (the owner, 2026-09-28:
   "similar honestly for when ... we get too rate limited and some service
   breaks"): the plain words for each refusal code, mirroring
   game/utils/serviceTrouble.js (servicePause.test.js holds them in step). */
/** @type {Record<string, [string, string]>} */
const TROUBLE_LINES = {
  busy: ['serviceTrouble.busy', 'Too many creations are running right now. No Stardust was spent. Try again in a few minutes.'],
  activeLimit: ['serviceTrouble.activeLimit', 'You already have as many Moon Fuse creations running as one account can. No Stardust was spent. Wait for one to finish.'],
  stellarActiveLimit: ['serviceTrouble.stellarActiveLimit', 'You already have as many Stellar cards being made as one account can. No Stardust was spent. Wait for one to finish.'],
  dreamActiveLimit: ['serviceTrouble.dreamActiveLimit', 'You already have as many Moon Dreams running as one account can. No Stardust was spent. Wait for one to finish.'],
  aiDown: ['serviceTrouble.aiDown', 'The AI service Moonfall uses isn\'t responding right now. We\'re working on it. No Stardust was spent.'],
  artDown: ['serviceTrouble.artDown', 'The art service Moonfall uses isn\'t responding right now. We\'re working on it. No Stardust was spent.'],
  videoDown: ['serviceTrouble.videoDown', 'The video service Stellar cards use isn\'t responding right now. We\'re working on it. No Stardust was spent.'],
  maintenance: ['serviceTrouble.maintenance', 'Moon Dream is down for maintenance. No Stardust was spent. Try again in a few minutes.'],
  ourSide: ['serviceTrouble.ourSide', 'Something went wrong on our side. No Stardust was spent. Try again in a few minutes.'],
};

/** @type {Record<string, string>} */
const TROUBLE_CODES = {
  llm_queue_full: 'busy',
  veo_queue_full: 'busy',
  moon_dream_summoner_admission_queue_full: 'busy',
  moon_dream_spell_admission_queue_full: 'busy',
  foundry_active_limit: 'activeLimit',
  veo_user_active_limit: 'stellarActiveLimit',
  moon_dream_user_active_limit: 'dreamActiveLimit',
  llm_queue_unavailable: 'aiDown',
  replicate_queue_unavailable: 'artDown',
  veo_queue_unavailable: 'videoDown',
  moon_dream_maintenance: 'maintenance',
  moon_dream_asset_cleanup_failed: 'ourSide',
  moon_dream_legacy_selection_cleanup_failed: 'ourSide',
};

/** @param {unknown} code */
function troubleKind(code) {
  const value = String(code || '');
  if (TROUBLE_CODES[value]) return TROUBLE_CODES[value];
  if (/^moon_dream_(?:summoner|spell)_admission_(?:already_queued|workflow_terminal)$/.test(value)) return 'ourSide';
  return null;
}

/**
 * The line a creation surface (Moon Fuse, Moon Dream, Stellar) shows for a
 * refusal that was Moonfall's rather than the player's: a pause, or a service
 * that is busy or down. Null for anything else, so the caller keeps its own.
 *
 * @param {{ code?: string, pause?: string, resumesAt?: number|null }|null|undefined} payload
 * @param {(key: string, fallback: string, replacements?: Record<string, string>) => string} t
 * @param {{ locale?: string, now?: number }} [options]
 * @returns {string|null}
 */
function creationRefusalLine(payload, t, options = {}) {
  if (isServicePause(payload)) return servicePauseCopy(payload, t, options).creation;
  if (payload?.code === 'payment_dispute_open') return accountOnHoldCopy({ passOnHold: false }, t).stardustLine;
  const kind = troubleKind(payload?.code);
  if (!kind) return null;
  const [key, fallback] = TROUBLE_LINES[kind];
  return t(key, fallback);
}

/* ON HOLD FOR A DISPUTED PAYMENT (the owner, 2026-09-28: "the artifact doesnt
   describe charismatic message for pausing paid sub features"). From the
   moment a bank dispute opens, the account plays as a free one and cannot
   spend Stardust (subscriptionTiers.withPaidFeaturesPaused) — and nothing
   said so: a Manga Pass simply vanished. These say it plainly: what is on
   hold, why, what still works, what comes back, and the one thing a player
   who disputed by mistake can do. Terms 3.3 says the same. */
/**
 * @param {{ passOnHold?: boolean }|null|undefined} status
 * @param {(key: string, fallback: string, replacements?: Record<string, string>) => string} t
 */
function accountOnHoldCopy(status, t) {
  const pass = status?.passOnHold === true;
  return {
    pass,
    title: pass
      ? t('accountOnHold.passTitle', 'Your pass is on hold')
      : t('accountOnHold.stardustTitle', 'Stardust spending is on hold'),
    body: pass
      ? t('accountOnHold.passBody', 'Your bank is reviewing a disputed payment on this account, so your pass and Stardust spending are on hold until it decides, and your pass won\'t renew. You can keep playing free meanwhile. If the bank sides with us, your pass comes back for the time you paid for. Disputed it by mistake? Ask your bank to withdraw the dispute.')
      : t('accountOnHold.stardustBody', 'Your bank is reviewing a disputed payment on this account, so Stardust spending and purchases are on hold until it decides. You can keep playing free meanwhile. Disputed it by mistake? Ask your bank to withdraw the dispute.'),
    // One line, where no dialog opens: the Moonlight card and wall.
    line: pass
      ? t('accountOnHold.passLine', 'Your pass is on hold while your bank reviews a disputed payment on this account. You can keep playing free meanwhile.')
      : t('accountOnHold.stardustLine', 'Stardust spending is on hold while your bank reviews a disputed payment on this account. No Stardust was spent.'),
    stardustLine: t('accountOnHold.stardustLine', 'Stardust spending is on hold while your bank reviews a disputed payment on this account. No Stardust was spent.'),
    purchasesLine: t('accountOnHold.purchasesLine', 'Purchases are on hold while your bank reviews a disputed payment on this account.'),
    // A bank's early fraud warning flagged the account (earlyFraudWarningData.js).
    flaggedLine: t('accountOnHold.flaggedLine', 'Purchases are blocked on this account because the card\'s bank reported a payment on it as unauthorized.'),
  };
}

const ACCOUNT_ON_HOLD_ID = 'account-on-hold';
const ACCOUNT_ON_HOLD_SEEN_KEY = 'moonfall:account-on-hold-explained';

/** Any other modal dialog on screen now (the store's is aria-hidden when shut). */
function anotherDialogOpen() {
  return [...document.querySelectorAll('[role="dialog"][aria-modal="true"], [role="alertdialog"][aria-modal="true"]')]
    .some((node) => node.getAttribute('aria-hidden') !== 'true' && node.getClientRects().length > 0);
}

let accountOnHoldExplained = false;

/**
 * Once per visit, when the Moonlight status first says the account is on hold
 * for a disputed payment, explain it. It waits for any other dialog to close
 * rather than stacking over it, and gives up after about a minute: the
 * Moonlight card, the wall and every refusal still say it.
 *
 * @param {{ paymentDisputeOnHold?: boolean, passOnHold?: boolean }|null|undefined} status
 * @param {{
 *   t: (key: string, fallback: string, replacements?: Record<string, string>) => string,
 *   anchor?: HTMLElement|null,
 *   retryMs?: number,
 *   maxTries?: number,
 * }} options
 */
function explainAccountOnHoldOnce(status, { t, anchor = null, retryMs = 1000, maxTries = 60 }) {
  if (status?.paymentDisputeOnHold !== true || accountOnHoldExplained) return;
  try {
    if (window.sessionStorage.getItem(ACCOUNT_ON_HOLD_SEEN_KEY) === '1') {
      accountOnHoldExplained = true;
      return;
    }
  } catch (_) { /* storage unavailable: once per page load instead */ }
  accountOnHoldExplained = true;
  let tries = 0;
  const attempt = () => {
    if (anotherDialogOpen()) {
      if ((tries += 1) < maxTries) window.setTimeout(attempt, retryMs);
      return;
    }
    openAccountOnHoldNotice({ status, t, anchor });
    try { window.sessionStorage.setItem(ACCOUNT_ON_HOLD_SEEN_KEY, '1'); } catch (_) { /* once per load */ }
  };
  attempt();
}

/** For tests: forget that this page load explained the hold. */
function resetAccountOnHoldForTests() {
  accountOnHoldExplained = false;
}

/* The control the open notice answers, so closing it can give the keyboard back. */
/** @type {HTMLElement|null} */
let returnFocusTo = null;

function closeServicePauseNotice() {
  const layer = document.getElementById(SERVICE_PAUSE_LAYER_ID)
    || document.getElementById(`${ACCOUNT_ON_HOLD_ID}-layer`);
  const focusWasInside = Boolean(layer?.contains?.(document.activeElement));
  if (layer && layer.parentNode) layer.parentNode.removeChild(layer);
  const target = returnFocusTo;
  returnFocusTo = null;
  if (focusWasInside && target?.isConnected && !(/** @type {HTMLButtonElement} */ (target)).disabled) {
    try { target.focus({ preventScroll: true }); } catch (_) { target.focus(); }
  }
}

/**
 * @param {string} tag
 * @param {string} [className]
 * @param {string} [textContent]
 */
function element(tag, className, textContent) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (textContent) node.textContent = textContent;
  return node;
}

/**
 * Open the pause notice. A free-account pause offers the passes; every
 * notice can be dismissed with OK, Escape or a press on the dim around it.
 *
 * @param {{
 *   payload: { pause?: string, resumesAt?: number|null },
 *   t: (key: string, fallback: string, replacements?: Record<string, string>) => string,
 *   anchor?: HTMLElement|null,
 *   locale?: string,
 *   now?: number,
 *   onSeePass?: () => void,
 * }} options
 */
function openServicePauseNotice({ payload, t, anchor = null, locale, now, onSeePass }) {
  const copy = servicePauseCopy(payload, t, { locale, now });
  return openNotice({
    id: SERVICE_PAUSE_ID,
    layerId: SERVICE_PAUSE_LAYER_ID,
    title: copy.title,
    body: copy.body,
    t,
    anchor,
    attributes: { 'data-pause': copy.pause },
    // A free player is offered the passes; a pause for everyone sells nothing.
    primary: copy.freeOnly && typeof onSeePass === 'function'
      ? { label: t('servicePause.seePasses', 'See passes'), onClick: onSeePass }
      : null,
  });
}

/**
 * The account-on-hold notice: once per visit, where the player can read it
 * whole. It sells nothing and asks nothing; it explains.
 *
 * @param {{
 *   status: { passOnHold?: boolean },
 *   t: (key: string, fallback: string, replacements?: Record<string, string>) => string,
 *   anchor?: HTMLElement|null,
 * }} options
 */
function openAccountOnHoldNotice({ status, t, anchor = null }) {
  const copy = accountOnHoldCopy(status, t);
  return openNotice({
    id: ACCOUNT_ON_HOLD_ID,
    layerId: `${ACCOUNT_ON_HOLD_ID}-layer`,
    title: copy.title,
    body: copy.body,
    t,
    anchor,
    attributes: { 'data-hold': copy.pass ? 'pass' : 'stardust' },
    primary: null,
  });
}

/**
 * One notice dialog on its own layer, dressed like the Moonlight wall: a
 * title, a reason in full sentences, an optional action and a dismiss.
 * Escape and a press on the dim around it close it too.
 *
 * @param {{
 *   id: string,
 *   layerId: string,
 *   title: string,
 *   body: string,
 *   t: (key: string, fallback: string, replacements?: Record<string, string>) => string,
 *   anchor?: HTMLElement|null,
 *   attributes?: Record<string, string>,
 *   primary?: { label: string, onClick: () => void }|null,
 * }} options
 */
function openNotice({ id, layerId, title, body: bodyText, t, anchor = null, attributes = {}, primary = null }) {
  closeServicePauseNotice();
  const layer = element('div', 'moonlight-wall-layer service-pause-layer');
  layer.id = layerId;
  const sheet = element('div', 'moonlight-wall service-pause');
  sheet.id = id;
  sheet.setAttribute('role', 'dialog');
  sheet.setAttribute('aria-modal', 'true');
  sheet.setAttribute('aria-labelledby', `${id}-title`);
  sheet.setAttribute('aria-describedby', `${id}-body`);
  for (const [name, value] of Object.entries(attributes)) sheet.setAttribute(name, value);
  sheet.tabIndex = -1;

  const heading = element('h2', 'moonlight-wall__title', title);
  heading.id = `${id}-title`;
  sheet.appendChild(heading);
  const body = element('p', 'service-pause__body', bodyText);
  body.id = `${id}-body`;
  sheet.appendChild(body);

  const actions = element('div', 'service-pause__actions');
  if (primary) {
    const action = element('button', 'moonlight-wall__action', primary.label);
    /** @type {HTMLButtonElement} */ (action).type = 'button';
    action.setAttribute('data-service-pause-action', 'pass');
    action.addEventListener('click', () => {
      // Out of the way first: what it opens must be the one dialog on screen.
      closeServicePauseNotice();
      primary.onClick();
    });
    actions.appendChild(action);
  }
  const ok = element('button', 'service-pause__ok', t('servicePause.ok', 'Got it'));
  /** @type {HTMLButtonElement} */ (ok).type = 'button';
  ok.setAttribute('data-service-pause-action', 'ok');
  ok.addEventListener('click', closeServicePauseNotice);
  actions.appendChild(ok);
  sheet.appendChild(actions);

  layer.appendChild(sheet);
  layer.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      closeServicePauseNotice();
      return;
    }
    if (event.key !== 'Tab') return;
    const nodes = [...sheet.querySelectorAll('button')].filter((node) => !node.disabled);
    if (!nodes.length) {
      event.preventDefault();
      return;
    }
    // WebKit on macOS may skip native button tab stops. Own every step,
    // including the first one from the dialog, so focus cannot escape.
    event.preventDefault();
    event.stopPropagation();
    const current = nodes.findIndex((node) => node === document.activeElement);
    const next = event.shiftKey
      ? (current <= 0 ? nodes.length - 1 : current - 1)
      : (current + 1) % nodes.length;
    nodes[next].focus({ preventScroll: true });
  });
  layer.addEventListener('click', (event) => {
    if (event.target === layer) closeServicePauseNotice();
  });
  document.body.appendChild(layer);
  returnFocusTo = anchor || null;
  try { sheet.focus({ preventScroll: true }); } catch (_) { sheet.focus(); }
  return sheet;
}

export {
  ACCOUNT_ON_HOLD_ID,
  explainAccountOnHoldOnce,
  resetAccountOnHoldForTests,
  SERVICE_PAUSE_ID,
  SERVICE_PAUSE_LAYER_ID,
  TROUBLE_CODES,
  accountOnHoldCopy,
  closeServicePauseNotice,
  creationRefusalLine,
  openAccountOnHoldNotice,
  formatResume,
  isServicePause,
  openServicePauseNotice,
  servicePauseCopy,
};
